import { TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { DUTY_PARENT_NOTE_MAX_LENGTH } from '../../home/constants/duty-parent-note.constants';

export type PatrolPlatformActivity =
  | AuthGen.DossierDiscordActivity
  | AuthGen.DossierRobloxActivity;

export type PatrolFamilyDutyScenario =
  | 'target'
  | 'aggressor'
  | 'mixed'
  | 'check_in';

export interface PatrolFamilyDutyPrefill {
  title: string;
  content: string;
  parentNote?: string;
  requiresParentParticipation: true;
}

export function shouldShowPatrolFamilyDutyCta(
  insights: AuthGen.DossierPatrolAiInsights | undefined,
  activity: PatrolPlatformActivity,
  familyDutyEnabled: boolean,
): boolean {
  if (!familyDutyEnabled || !insights) {
    return false;
  }

  const hasNarrative = Boolean(
    insights.targetSummary?.trim() ||
      insights.aggressorSummary?.trim() ||
      (insights.guidance?.length ?? 0) > 0,
  );
  if (!hasNarrative) {
    return false;
  }

  const trend = (insights.safetyTrend ?? '').toLowerCase();
  if (
    trend.includes('concerning') ||
    trend === 'mixed' ||
    trend === 'negative'
  ) {
    return true;
  }

  const targetCount = activity.asTarget?.totalIncidents ?? 0;
  const modEvents = activity.asAggressor?.totalModerationEvents ?? 0;
  const harmEvents = activity.asAggressor?.totalHarmTowardOthersIncidents ?? 0;
  if (targetCount > 0 || modEvents > 0 || harmEvents > 0) {
    return true;
  }

  return (insights.guidance?.length ?? 0) > 0;
}

export function resolvePatrolFamilyDutyScenario(
  insights: AuthGen.DossierPatrolAiInsights,
  activity: PatrolPlatformActivity,
): PatrolFamilyDutyScenario {
  const trend = (insights.safetyTrend ?? '').toLowerCase();
  if (trend === 'concerning_as_target') {
    return 'target';
  }
  if (trend === 'concerning_as_aggressor') {
    return 'aggressor';
  }
  if (trend === 'mixed') {
    return 'mixed';
  }

  const targetCount = activity.asTarget?.totalIncidents ?? 0;
  const modEvents = activity.asAggressor?.totalModerationEvents ?? 0;
  const harmEvents = activity.asAggressor?.totalHarmTowardOthersIncidents ?? 0;

  if (targetCount > 0 && (modEvents > 0 || harmEvents > 0)) {
    return 'mixed';
  }
  if (targetCount > 0) {
    return 'target';
  }
  if (modEvents > 0 || harmEvents > 0) {
    return 'aggressor';
  }
  return 'check_in';
}

export function buildPatrolFamilyDutyPrefill(
  translate: TranslateService,
  insights: AuthGen.DossierPatrolAiInsights,
  activity: PatrolPlatformActivity,
  platformId: 'discord' | 'roblox',
): PatrolFamilyDutyPrefill {
  const scenario = resolvePatrolFamilyDutyScenario(insights, activity);
  const platformKey =
    platformId === 'roblox'
      ? 'app.parentPatrol.platforms.roblox.title'
      : 'app.parentPatrol.platforms.discord.title';
  const platform = translate.instant(platformKey);

  const title = translate.instant(
    `app.parentPatrol.familyDutyPrefill.${scenario}.title`,
    { platform },
  );
  const content = translate.instant(
    `app.parentPatrol.familyDutyPrefill.${scenario}.content`,
    { platform },
  );
  const parentNote = resolvePatrolParentNote(translate, scenario, insights.guidance);

  return {
    title,
    content,
    parentNote,
    requiresParentParticipation: true,
  };
}

function resolvePatrolParentNote(
  translate: TranslateService,
  scenario: PatrolFamilyDutyScenario,
  guidance?: string[],
): string | undefined {
  const tip = guidance?.find((entry) => entry?.trim())?.trim();
  if (tip && tip.length <= DUTY_PARENT_NOTE_MAX_LENGTH) {
    return tip;
  }

  const fromI18n = translate.instant(
    `app.parentPatrol.familyDutyPrefill.${scenario}.parentNote`,
  );
  if (typeof fromI18n !== 'string') {
    return undefined;
  }
  const trimmed = fromI18n.trim();
  return trimmed ? trimmed.slice(0, DUTY_PARENT_NOTE_MAX_LENGTH) : undefined;
}
